function kitSrcset(src, widths = [300, 400, 600, 800]) {
  return widths.map((w) => `${src.replace(/(\.(jpg|jpeg|png|gif|webp))(\?.*)?$/i, `_${w}x$1$3`)} ${w}w`).join(',');
}

/** From `theme-price-utils.js` — load that asset before this file in Liquid. */
const kitFormatMoney =
  typeof window.themeFormatMoney === 'function'
    ? window.themeFormatMoney
    : function kitFormatMoneyFallback(cents) {
        if (cents == null || !Number.isFinite(cents)) {
          return '';
        }
        return (cents / 100).toFixed(2);
      };

if (!customElements.get('kit-product')) {
  class KitProduct extends HTMLElement {
    connectedCallback() {
      this.productVariants = this.querySelector('product-variants');
      this.titleEl = this.querySelector('[data-js-product-title]');
      if (!this.productVariants) return;

      this.productVariants.addEventListener('variant:changed', (e) => this.onVariantChanged(e));
      this.productVariants.addEventListener('variant:working', (e) => this.onVariantWorking(e));
      this.productVariants.addEventListener('combined-listing:changed', (e) => this.onCombinedListingChanged(e));
      this.productVariants.addEventListener('combined-listing:working', (e) => this.onCombinedListingWorking(e));

      this._updated = new Event('kit-product:updated', {bubbles: true});
      this._working = new Event('kit-product:working', {bubbles: true});

      this.imageEl = this.querySelector('.kit-product__image img');
      this.zoomEl = this.querySelector('product-image-zoom');
      this.errorEl = this.querySelector('[data-kit-error]');
      this.priceEl = this.querySelector('.shop-the-look__price [data-price]');
      this.comparePriceEl = this.querySelector('.shop-the-look__price [data-compare-price]');

      this._computeStatus();

      const resync = () => {
        if (!this.isConnected) return;
        this._computeStatus();
        this.updatePrice();
        this.dispatchEvent(this._updated);
      };

      queueMicrotask(resync);

      // `component-product-form.js` (product-variants) is async on product templates while
      // this file is defer — re-sync when the class exists and at window load.
      customElements.whenDefined('product-variants').then(() => {
        queueMicrotask(resync);
      });
      window.addEventListener('load', resync, {once: true});
    }

    get status() {
      return this._status;
    }

    _optionGroupHasSelection(fieldset) {
      if (fieldset.querySelector(':checked')) {
        return true;
      }

      const firstRadio = fieldset.querySelector('input[type="radio"]');
      if (!firstRadio) {
        return true;
      }

      const {name: groupName} = firstRadio;
      for (const input of this.productVariants.querySelectorAll('input[type="radio"]')) {
        if (input.name === groupName && input.checked) {
          return true;
        }
      }
      return false;
    }

    _getVariantData() {
      const el = this.productVariants;
      if (el) {
        try {
          const v = el.variant;
          if (v && typeof v === 'object' && v.id != null) {
            return v;
          }
        } catch {
          /* not yet upgraded */
        }
      }
      const script = el?.querySelector('script.variant-data[type="application/json"]');
      if (script?.textContent) {
        try {
          const v = JSON.parse(script.textContent);
          if (v && typeof v === 'object' && v.id != null) {
            return v;
          }
        } catch {
          /* ignore */
        }
      }
      try {
        if (el?.variant) {
          return el.variant;
        }
      } catch {
        return null;
      }
      return null;
    }

    _computeStatus() {
      const variant = this._getVariantData();
      const complete = [...this.productVariants.querySelectorAll('fieldset')].every((fs) =>
        this._optionGroupHasSelection(fs)
      );

      this._status = {
        complete,
        available: complete && !!variant?.id && !!variant?.available,
        variant: variant || undefined,
      };
    }

    onVariantWorking() {
      this.dispatchEvent(this._working);
    }

    onCombinedListingWorking() {
      this.productVariants.sectionId = 'kit-scripts';
      this.dispatchEvent(this._working);
    }

    onCombinedListingChanged(e) {
      this._computeStatus();
      this.updateTitle(e);
      this.updateImage(e);
      this.handleErrors(e);
      this.updatePrice();
      this.dispatchEvent(this._updated);
    }

    onVariantChanged(e) {
      this._computeStatus();
      const variant = e.target?.variant;
      if (variant) {
        this.updateImage(e);
        this.handleErrors(e);
      }
      this.updatePrice();
      this.dispatchEvent(this._updated);
    }

    handleErrors(e) {
      const variant = e.target.variant;
      if (!variant) {
        return;
      }

      if (!variant.available) {
        this.errorEl.textContent =
          this.closest('kit-container')?.dataset.kitErrorUnavailable ||
          'This option is currently out of stock. Try another combination.';
        this.errorEl.style.display = 'block';
      } else {
        this.errorEl.style.display = 'none';
        this.errorEl.textContent = '';
      }
    }

    updateTitle(e) {
      const variant = e.target.variant;
      if (!variant?.product_title) {
        return;
      }
      this.titleEl.textContent = variant.product_title;
    }

    updateImage(e) {
      const variant = e.target.variant;
      if (!variant?.featured_image_src) {
        return;
      }

      const src = variant.featured_image_src;
      this.imageEl.src = src;
      this.imageEl.srcset = kitSrcset(src);
      this.imageEl.sizes = '(max-width: 768px) 100px, 50px';
      this.imageEl.width = 240;
      this.imageEl.height = 240;
      this.imageEl.loading = 'lazy';
      this.imageEl.onload = () => {
        this.imageEl.classList.add('lazyloaded');
      };
      this.zoomEl?.setAttribute('data-image', src);
    }

    updatePrice() {
      if (!this.priceEl) {
        return;
      }

      const v = this._getVariantData();

      if (!v || typeof v.price !== 'number' || !Number.isFinite(v.price)) {
        this.priceEl.textContent = '';
        if (this.comparePriceEl) {
          this.comparePriceEl.textContent = '';
          this.comparePriceEl.hidden = true;
        }
        return;
      }

      this.priceEl.textContent = kitFormatMoney(v.price);

      if (this.comparePriceEl) {
        const compare = v.compare_at_price;
        if (typeof compare === 'number' && compare > v.price && compare > 0) {
          this.comparePriceEl.textContent = kitFormatMoney(compare);
          this.comparePriceEl.hidden = false;
        } else {
          this.comparePriceEl.textContent = '';
          this.comparePriceEl.hidden = true;
        }
      }
    }
  }

  customElements.define('kit-product', KitProduct);
}

if (!customElements.get('kit-container')) {
  const INCLUDE_SELECTOR = '[data-shop-the-look-include]';
  const INCLUDE_ALL_SELECTOR = '[data-shop-the-look-include-all]';

  class KitContainer extends HTMLElement {
    connectedCallback() {
      this.btn = this.querySelector('[data-kit-add-to-cart]');
      this.btnText = this.querySelector('[data-kit-add-to-cart-text]');
      this.totalPriceEl = this.querySelector('[data-kit-total-price]');
      this.itemCountEl = this.querySelector('[data-kit-item-count]');
      if (!this.btn) {
        return;
      }

      this._onKitProductUpdated = () => {
        this.updateState();
      };
      this._onIncludeChange = (e) => {
        const target = e.target;
        if (target?.matches?.(INCLUDE_ALL_SELECTOR)) {
          this._applySelectAll(target.checked);
          this.updateState();
          return;
        }
        if (target?.matches?.(INCLUDE_SELECTOR)) {
          this._syncSelectAll();
          this.updateState();
        }
      };

      this.btn.addEventListener('click', () => this.addToCart());
      for (const kit of this.kitProducts) {
        kit.addEventListener('kit-product:updated', this._onKitProductUpdated);
      }
      // Selectable kit-products toggle inclusion via a checkbox; listen at the
      // container so newly-added rows or re-rendered fragments are covered.
      this.addEventListener('change', this._onIncludeChange);
      this._syncSelectAll();

      const sync = () => {
        if (!this.isConnected) {
          return;
        }
        this.updateState();
      };
      queueMicrotask(sync);
      customElements.whenDefined('product-variants').then(() => {
        queueMicrotask(sync);
      });
      window.addEventListener('load', sync, {once: true});
    }

    disconnectedCallback() {
      this.removeEventListener('change', this._onIncludeChange);
    }

    get kitProducts() {
      return [...this.querySelectorAll('kit-product')];
    }

    /**
     * Selection mode is active when at least one kit-product carries an
     * include checkbox. In that mode only checked products are queued for
     * add-to-cart. Otherwise (legacy kit block) every product is required.
     */
    get isSelectable() {
      return !!this.querySelector(INCLUDE_SELECTOR);
    }

    _includeCheckbox(kp) {
      return kp.querySelector(INCLUDE_SELECTOR);
    }

    _isIncluded(kp) {
      const checkbox = this._includeCheckbox(kp);
      if (!checkbox) {
        return true;
      }
      return checkbox.checked && !checkbox.disabled;
    }

    get _includeAllCheckbox() {
      return this.querySelector(INCLUDE_ALL_SELECTOR);
    }

    get _includeCheckboxes() {
      return [...this.querySelectorAll(INCLUDE_SELECTOR)];
    }

    get _enabledIncludeCheckboxes() {
      return this._includeCheckboxes.filter((cb) => !cb.disabled);
    }

    get activeKitProducts() {
      return this.isSelectable ? this.selectedKitProducts : this.kitProducts;
    }

    /**
     * Drive every per-row include checkbox from the "Shop All" toggle. Each
     * change event is dispatched so any listeners (CSS `:has`, observers) see
     * the new state, just as if the user clicked the row directly.
     */
    _applySelectAll(checked) {
      for (const cb of this._includeCheckboxes) {
        if (cb.disabled || cb.checked === checked) continue;
        cb.checked = checked;
        cb.dispatchEvent(new Event('change', {bubbles: true}));
      }
    }

    /**
     * Reflect the per-row selection back into the "Shop All" checkbox: checked
     * only when every (enabled) row is checked, indeterminate when partial.
     */
    _syncSelectAll() {
      const all = this._includeAllCheckbox;
      if (!all) return;
      const rows = this._enabledIncludeCheckboxes;
      if (rows.length === 0) {
        all.checked = false;
        all.indeterminate = false;
        return;
      }
      const checkedCount = rows.filter((cb) => cb.checked).length;
      all.checked = checkedCount === rows.length;
      all.indeterminate = checkedCount > 0 && checkedCount < rows.length;
    }

    get selectedKitProducts() {
      return this.kitProducts.filter((kp) => this._isIncluded(kp));
    }

    get isReady() {
      const products = this.activeKitProducts;
      return products.length > 0 && products.every((kp) => kp.status?.available);
    }

    updateState() {
      const selectable = this.isSelectable;
      const targets = this.activeKitProducts;

      const allComplete = targets.length > 0 && targets.every((kp) => kp.status?.complete);
      const allAvailable = allComplete && targets.every((kp) => kp.status?.available);
      const ready = targets.length > 0 && allAvailable;

      this.btn.disabled = !ready;
      this.btn.classList.toggle('disabled', !ready);

      this.updateTotalPrice();

      if (!this.btnText) {
        return;
      }

      if (selectable && targets.length === 0) {
        this.btnText.textContent = this.dataset.kitLabelSelect || 'Select Kit Items';
      } else if (!allComplete || !allAvailable) {
        this.btnText.textContent = this.dataset.kitLabelChoose || 'Choose Options';
      } else {
        this.btnText.textContent =
          this.dataset.kitLabelAdd ||
          (this.classList.contains('shop-the-look')
            ? 'Add to bag'
            : this.dataset.kitLabelAddFallback || 'Add Kit to Cart');
      }
    }

    /**
     * Sum variant prices (Shopify cents) for included kit rows; mirrors add-to-cart selection.
     */
    updateTotalPrice() {
      if (this.totalPriceEl) {
        const targets = this.activeKitProducts;

        let totalCents = 0;
        for (const kp of targets) {
          const cents = kp.status?.variant?.price;
          if (typeof cents === 'number' && Number.isFinite(cents)) {
            totalCents += cents;
          }
        }

        this.totalPriceEl.textContent = kitFormatMoney(totalCents);
      }

      this._updateItemCount();
    }

    _updateItemCount() {
      if (!this.itemCountEl) {
        return;
      }
      const targets = this.activeKitProducts;
      const n = targets.length;
      const one = this.dataset.kitCountOne || "1 item";
      const otherTpl = this.dataset.kitCountOther || "{{ count }} items";
      this.itemCountEl.textContent =
        n === 1 ? one : otherTpl.replace(/\{\{\s*count\s*\}\}/g, String(n));
    }

    async addToCart() {
      if (!this.isReady) {
        return;
      }

      const kitId = `kit_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
      const kitTitle = this.dataset.kitTitle || 'Kit';
      const targets = this.activeKitProducts;

      // Items added from the shop-the-look dialog (HTW entry point) get their
      // own attribute keys so they're distinguishable from standalone kit adds
      // in the cart / order data.
      const fromHTWDialog = !!this.closest('custom-shop-the-look');
      const props = fromHTWDialog
        ? {_htw_id: kitId, _htw_title: kitTitle}
        : {_kit_id: kitId, _kit_title: kitTitle};

      const items = targets.map((kp) => ({
        id: kp.status.variant.id,
        quantity: 1,
        properties: props,
      }));

      try {
        this.btn.classList.add('working');
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {'Content-Type': 'application/json'},
          body: JSON.stringify({items}),
        });

        if (!res.ok) {
          const errorData = await res.json().catch(() => ({}));
          throw new Error(errorData.description || `HTTP ${res.status}`);
        }

        const result = await res.json();

        if (
          this.closest('custom-shop-the-look') &&
          typeof window.ShopTheLookOpener?.hide === 'function'
        ) {
          window.ShopTheLookOpener.hide();
        }

        document.dispatchEvent(new CustomEvent('cart:updated', {detail: {items: result}}));
        document.dispatchEvent(new CustomEvent('add-to-cart', {detail: {items: result}}));
        window.refreshCart?.(true, true);
      } catch (e) {
        console.error('[kit-container] add to cart error:', e);
      } finally {
        this.btn.classList.remove('working');
      }
    }
  }

  customElements.define('kit-container', KitContainer);
}

if (!customElements.get('kit-gallery-manager')) {
  class KitGalleryManager extends HTMLElement {
    connectedCallback() {
      this.grid = this.querySelector('[data-kit-gallery-grid]');
      if (!this.grid) {
        return;
      }

      this._parent = this.closest('kit-parent') || this.closest('kit-container');
      if (!this._parent) {
        return;
      }

      this._onUpdate = (e) => {
        const kp = e.target.closest?.('kit-product') || e.target;
        this.updateSlide(kp);
        if (window.innerWidth < 768) {
          this.scrollToProduct(kp);
        }
      };
      this._parent.addEventListener('kit-product:updated', this._onUpdate);

      requestAnimationFrame(() => {
        this.updateGallery();
      });
    }

    disconnectedCallback() {
      this._parent?.removeEventListener('kit-product:updated', this._onUpdate);
    }

    get kitProducts() {
      return this._parent ? [...this._parent.querySelectorAll('kit-product')] : [];
    }

    slideForProduct(kp) {
      const index = this.kitProducts.indexOf(kp);
      return index >= 0 ? this.grid.querySelector(`[data-kit-slide="${index}"]`) : null;
    }

    updateGallery() {
      for (const kp of this.kitProducts) {
        this.updateSlide(kp);
      }
    }

    updateSlide(kp) {
      const slide = this.slideForProduct(kp);
      if (!slide) {
        return;
      }

      const status = kp.status;
      if (!status) {
        if (!slide.classList.contains('product-gallery-item--placeholder')) {
          this.renderPlaceholder(slide);
        }
        return;
      }

      const src = status.variant?.featured_image_src || kp.querySelector('.kit-product__image img')?.src;

      if (!src) {
        if (!slide.classList.contains('product-gallery-item--placeholder')) {
          this.renderPlaceholder(slide);
        }
        return;
      }

      const currentImg = slide.querySelector('img');
      const isPartial = !status.complete;
      const srcChanged = !currentImg || currentImg.src !== src;
      const wasPlaceholder = slide.classList.contains('product-gallery-item--placeholder');

      if (srcChanged || wasPlaceholder) {
        this.renderImage(slide, src, status.variant, isPartial);
      } else {
        slide.classList.toggle('product-gallery-item--partial', isPartial);
        const callout = slide.querySelector('.kit-gallery__callout');
        if (isPartial && !callout) {
          this.appendCallout(slide);
        } else if (!isPartial && callout) {
          callout.remove();
        }
      }
    }

    renderPlaceholder(slide) {
      slide.classList.add('product-gallery-item--placeholder');
      slide.classList.remove('product-gallery-item--partial');
      const title = slide.dataset.productTitle || 'your selection';
      const idx = slide.dataset.kitSlide;
      const plus =
        KROWN?.settings?.symbols?.plus ||
        '<svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M12 5V19M5 12H19" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

      slide.innerHTML = `
        <figure class="lazy-image lazy-image--background">
          <div class="kit-gallery__placeholder-content">${plus}<span>Select ${title}</span></div>
        </figure>`;

      this.appendCallout(slide, idx);
    }

    renderImage(slide, src, variant, isPartial) {
      slide.classList.remove('product-gallery-item--placeholder');
      slide.classList.toggle('product-gallery-item--partial', isPartial);
      const cols = this.kitProducts.length <= 2 ? 2 : 3;

      slide.innerHTML = `
        <figure class="lazy-image lazy-image--zoom-animation lazy-image--background">
          <img src="${src}" srcset="${kitSrcset(src)}" sizes="(min-width: 768px) ${100 / cols}vw, 100vw"
            alt="${variant?.name || ''}" width="400" height="480" loading="lazy"
            onload="this.parentNode.classList.add('lazyloaded')" />
        </figure>
        <product-image-zoom class="product-gallery-item__zoom" data-image="${src}">
          ${KROWN?.settings?.symbols?.zoom_in || ''}
        </product-image-zoom>`;

      if (isPartial) {
        this.appendCallout(slide);
      }
    }

    appendCallout(slide, idx) {
      const index = idx ?? slide.dataset.kitSlide;
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'kit-gallery__callout';
      btn.dataset.slideIndex = index;
      btn.textContent = 'Select Size And Color';
      btn.addEventListener('click', () => {
        this.handleCalloutClick(+index);
      });
      slide.appendChild(btn);
    }

    scrollToProduct(kp) {
      const slide = this.slideForProduct(kp);
      if (!slide) {
        return;
      }
      requestAnimationFrame(() => {
        this.grid.scrollTo({left: slide.offsetLeft, behavior: 'smooth'});
      });
    }

    handleCalloutClick(index) {
      const kp = this.kitProducts[index];
      if (!kp) {
        return;
      }
      kp.scrollIntoView({behavior: 'smooth', block: 'start'});
      setTimeout(() => {
        kp.querySelector('input[type="radio"]')?.focus();
      }, 500);
    }
  }

  customElements.define('kit-gallery-manager', KitGalleryManager);
}
